export type SlovakRegion =
  | 'BRATISLAVSKY'
  | 'TRNAVSKY'
  | 'TRENCIANSKY'
  | 'NITRIANSKY'
  | 'ZILINSKY'
  | 'BANSKOBYSTRICKY'
  | 'PRESOVSKY'
  | 'KOSICKY'

export const SLOVAK_REGIONS: SlovakRegion[] = [
  'BRATISLAVSKY',
  'TRNAVSKY',
  'TRENCIANSKY',
  'NITRIANSKY',
  'ZILINSKY',
  'BANSKOBYSTRICKY',
  'PRESOVSKY',
  'KOSICKY',
]

export const REGION_LABELS: Record<SlovakRegion, string> = {
  BRATISLAVSKY: 'Bratislavský kraj',
  TRNAVSKY: 'Trnavský kraj',
  TRENCIANSKY: 'Trenčiansky kraj',
  NITRIANSKY: 'Nitriansky kraj',
  ZILINSKY: 'Žilinský kraj',
  BANSKOBYSTRICKY: 'Banskobystrický kraj',
  PRESOVSKY: 'Prešovský kraj',
  KOSICKY: 'Košický kraj',
}

export const REGION_DISTRICTS: Record<SlovakRegion, string[]> = {
  BRATISLAVSKY: [
    'Bratislava I', 'Bratislava II', 'Bratislava III', 'Bratislava IV', 'Bratislava V',
    'Malacky', 'Pezinok', 'Senec',
  ],
  TRNAVSKY: [
    'Dunajská Streda', 'Galanta', 'Hlohovec', 'Piešťany', 'Senica', 'Skalica', 'Trnava',
  ],
  TRENCIANSKY: [
    'Bánovce nad Bebravou', 'Ilava', 'Myjava', 'Nové Mesto nad Váhom', 'Partizánske',
    'Považská Bystrica', 'Prievidza', 'Púchov', 'Trenčín',
  ],
  NITRIANSKY: [
    'Komárno', 'Levice', 'Nitra', 'Nové Zámky', 'Šaľa', 'Topoľčany', 'Zlaté Moravce',
  ],
  ZILINSKY: [
    'Bytča', 'Čadca', 'Dolný Kubín', 'Kysucké Nové Mesto', 'Liptovský Mikuláš',
    'Martin', 'Námestovo', 'Ružomberok', 'Turčianske Teplice', 'Tvrdošín', 'Žilina',
  ],
  BANSKOBYSTRICKY: [
    'Banská Bystrica', 'Banská Štiavnica', 'Brezno', 'Detva', 'Krupina', 'Lučenec',
    'Poltár', 'Revúca', 'Rimavská Sobota', 'Veľký Krtíš', 'Zvolen', 'Žarnovica',
    'Žiar nad Hronom',
  ],
  PRESOVSKY: [
    'Bardejov', 'Humenné', 'Kežmarok', 'Levoča', 'Medzilaborce', 'Poprad', 'Prešov',
    'Sabinov', 'Snina', 'Stará Ľubovňa', 'Stropkov', 'Svidník', 'Vranov nad Topľou',
  ],
  KOSICKY: [
    'Gelnica', 'Košice I', 'Košice II', 'Košice III', 'Košice IV', 'Košice-okolie',
    'Michalovce', 'Rožňava', 'Sobrance', 'Spišská Nová Ves', 'Trebišov',
  ],
}

export function isValidDistrict(region: SlovakRegion, district: string): boolean {
  return REGION_DISTRICTS[region]?.includes(district) ?? false
}

/**
 * Mestá (obce) zoskupené podľa kraja. Jediný zdroj pravdy zdieľaný medzi:
 *  - admin Reality Monitor filtrom (kraj → zúženie dropdownu miest),
 *  - monitor scraperom (validácia/extrakcia mesta z textu inzerátu).
 */
export const REGION_CITIES: Record<SlovakRegion, string[]> = {
  BRATISLAVSKY: [
    'Bratislava', 'Malacky', 'Pezinok', 'Senec', 'Stupava', 'Svätý Jur', 'Modra',
  ],
  TRNAVSKY: [
    'Trnava', 'Dunajská Streda', 'Galanta', 'Hlohovec', 'Piešťany', 'Senica', 'Skalica',
    'Leopoldov', 'Sereď', 'Šamorín', 'Šaštín-Stráže', 'Gabčíkovo', 'Vrbové', 'Veľké Úľany',
    'Holíč', 'Gbely', 'Sládkovičovo',
  ],
  TRENCIANSKY: [
    'Trenčín', 'Bánovce nad Bebravou', 'Ilava', 'Myjava', 'Nové Mesto nad Váhom',
    'Partizánske', 'Považská Bystrica', 'Prievidza', 'Púchov', 'Dubnica nad Váhom',
    'Handlová', 'Nováky', 'Stará Turá', 'Bojnice', 'Nemšová', 'Brezová pod Bradlom',
    'Trenčianske Teplice', 'Nová Dubnica',
  ],
  NITRIANSKY: [
    'Nitra', 'Komárno', 'Levice', 'Nové Zámky', 'Šaľa', 'Topoľčany', 'Zlaté Moravce',
    'Kolárovo', 'Štúrovo', 'Šurany', 'Vráble', 'Hurbanovo', 'Želiezovce', 'Tlmače',
    'Šahy', 'Tvrdošovce', 'Dvory nad Žitavou',
  ],
  ZILINSKY: [
    'Žilina', 'Bytča', 'Čadca', 'Dolný Kubín', 'Kysucké Nové Mesto', 'Liptovský Mikuláš',
    'Martin', 'Námestovo', 'Ružomberok', 'Turčianske Teplice', 'Tvrdošín', 'Vrútky',
    'Turzovka', 'Rajec', 'Liptovský Hrádok', 'Trstená', 'Krásno nad Kysucou',
  ],
  BANSKOBYSTRICKY: [
    'Žiar nad Hronom', 'Banská Bystrica', 'Banská Štiavnica', 'Brezno', 'Detva', 'Krupina',
    'Lučenec', 'Poltár', 'Revúca', 'Rimavská Sobota', 'Veľký Krtíš', 'Zvolen', 'Žarnovica',
    'Fiľakovo', 'Tornaľa', 'Hnúšťa', 'Tisovec', 'Sliač', 'Dudince',
    'Kremnica', 'Nová Baňa', 'Modrý Kameň', 'Jelšava',
  ],
  PRESOVSKY: [
    'Prešov', 'Bardejov', 'Humenné', 'Kežmarok', 'Levoča', 'Medzilaborce', 'Poprad',
    'Sabinov', 'Snina', 'Stará Ľubovňa', 'Stropkov', 'Svidník', 'Vranov nad Topľou',
    'Vysoké Tatry', 'Spišská Belá', 'Spišské Podhradie',
    'Svit', 'Lipany', 'Giraltovce', 'Hanušovce nad Topľou', 'Podolínec', 'Spišská Stará Ves',
  ],
  KOSICKY: [
    'Košice', 'Gelnica', 'Michalovce', 'Rožňava', 'Sobrance', 'Trebišov',
    'Moldava nad Bodvou', 'Kráľovský Chlmec', 'Sečovce', 'Veľké Kapušany',
    'Čierna nad Tisou', 'Dobšiná', 'Strážske', 'Spišská Nová Ves', 'Medzev',
  ],
}

const REGION_BY_LABEL: Record<string, SlovakRegion> = Object.fromEntries(
  SLOVAK_REGIONS.map((r) => [REGION_LABELS[r], r]),
) as Record<string, SlovakRegion>

/**
 * Region representation differs across the system: filters store the enum code
 * (`'ZILINSKY'`), monitored listings store the human label (`'Žilinský kraj'`).
 * These helpers translate between the two so the feed/match comparisons line up.
 * Both are idempotent — an unrecognised value passes through unchanged.
 */
export function regionCodeToLabel(region: string): string {
  return (REGION_LABELS as Record<string, string>)[region] ?? region
}
export function regionLabelToCode(region: string): string {
  return REGION_BY_LABEL[region] ?? region
}

/** Plochý, abecedne zoradený zoznam všetkých miest (zo všetkých krajov). */
export const SLOVAK_CITIES: string[] = SLOVAK_REGIONS
  .flatMap((r) => REGION_CITIES[r])
  .sort((a, b) => a.localeCompare(b, 'sk'))

/**
 * Vráti zoradený zoznam miest pre daný kraj. Prijíma enum (`'KOSICKY'`)
 * aj label (`'Košický kraj'`). Pre neznámy kraj vráti prázdne pole.
 */
export function getCitiesByRegion(region: SlovakRegion | string): string[] {
  const key = (region in REGION_CITIES ? region : REGION_BY_LABEL[region]) as SlovakRegion | undefined
  const cities = key ? REGION_CITIES[key] : undefined
  return cities ? [...cities].sort((a, b) => a.localeCompare(b, 'sk')) : []
}

/**
 * Vráti abecedne zoradený zoznam okresov pre daný kraj. Prijíma enum (`'KOSICKY'`)
 * aj label (`'Košický kraj'`). Pre neznámy kraj vráti prázdne pole. Zdroj: REGION_DISTRICTS.
 */
export function getDistrictsByRegion(region: SlovakRegion | string): string[] {
  const key = (region in REGION_DISTRICTS ? region : REGION_BY_LABEL[region]) as SlovakRegion | undefined
  const districts = key ? REGION_DISTRICTS[key] : undefined
  return districts ? [...districts].sort((a, b) => a.localeCompare(b, 'sk')) : []
}
